import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * usePaletteSearch — encapsulates the three moving parts of CommandPalette:
 *   1. Role-filtered + query-matched local page registry results
 *   2. Debounced remote /search query with abort-on-unmount
 *   3. Keyboard navigation (arrow up/down + enter + escape) across the
 *      combined result list
 *
 * Exposes a clean surface so the UI component can stay purely presentational.
 */
export default function usePaletteSearch({
  isOpen,
  role,
  pageRegistry,
  axiosAuth,
  onClose,
  onSelect,
  debounceMs = 250,
  minRemoteChars = 2,
  defaultLimit = 8,
}) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [remoteResults, setRemoteResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // 1. Role-filtered local page registry
  const filteredPages = useMemo(() => {
    const allowed = pageRegistry.filter(p => p.roles.includes(role));
    if (!query.trim()) return allowed.slice(0, defaultLimit);
    const q = query.toLowerCase();
    return allowed.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.subtitle.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  }, [query, role, pageRegistry, defaultLimit]);

  const allResults = useMemo(
    () => [...filteredPages, ...remoteResults],
    [filteredPages, remoteResults]
  );

  // 2. Debounced remote search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.length < minRemoteChars) {
      setRemoteResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axiosAuth().get(
          `/search?q=${encodeURIComponent(query)}`
        );
        setRemoteResults(res.data?.results || []);
      } catch {
        setRemoteResults([]);
      } finally {
        setSearching(false);
      }
    }, debounceMs);
    return () => clearTimeout(debounceRef.current);
  }, [query, axiosAuth, debounceMs, minRemoteChars]);

  // Reset on open toggle
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setRemoteResults([]);
    }
  }, [isOpen]);

  // Reset cursor when result count shifts
  useEffect(() => { setActiveIndex(0); }, [allResults.length]);

  // 3. Keyboard nav
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allResults[activeIndex]) {
      e.preventDefault();
      onSelect?.(allResults[activeIndex]);
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  }, [allResults, activeIndex, onSelect, onClose]);

  return {
    query,
    setQuery,
    activeIndex,
    setActiveIndex,
    remoteResults,
    searching,
    filteredPages,
    allResults,
    handleKeyDown,
  };
}
